import React, { FC, memo, useContext } from 'react';
import { Document, Page, Text, View } from '@react-pdf/renderer';

import styles, { BASE_FONT_SIZE } from '../pdfStyles';
import PdfLogoComponent from '../PdfComponents/PdfLogo';
import PdfFooter from '../PdfComponents/PdfFooter';
import PdfHeaderDetails from '../PdfComponents/PdfHeaderDetails';
import PdfMainDetails from '../PdfComponents/PdfMainDetails';
import PdfTitleTextBox from '../PdfComponents/PdfTitleTextBox';
import { PdfRemarksSection } from '../PdfComponents/PdfSection';
import PdfPassengerDetails from '../PdfComponents/PdfPassengerDetails';
import PdfTableComponent from '../PdfComponents/PdfTable';
import PdfBillingContactDetails from '../PdfComponents/PdfBillingContactDetails';
import { Context as ClientsContext } from '../../../api/clients/context';
import { Client } from '../../../api/clients/types';

import { VouchersPdfPropsType } from './voucherHelpers';

const CreditDebitPdf: FC<VouchersPdfPropsType> = (props: VouchersPdfPropsType) => {
    const clientsContext = useContext(ClientsContext);
    const {
        info: {
            dueDate,
            travelFileNumber,
            travelFileOpenDate,
            travelFileStartDate,
            remark,
            for: forLabel,
            pdfInfo: {
                companyInfo,
                userName,
                showIssuedBy,
                voucherCertification = 'Draft',
                pdfImageSource = (clientsContext.selected as Client)?.companyLogoUrl,
                passenger,
                agentInfo,
                supplierInfo,
                columns,
                rows,
                summaryLine,
                topLeftDetailsText,
                creditOrDebit
            }
        },
        number: voucherNumber,
        type: voucherType,
        createdAt
    } = props;

    return (
        <Document title={`${voucherType} ${voucherNumber || ''}`}>
            <Page wrap size="A4" style={styles.page}>
                <PdfLogoComponent pdfImageSource={pdfImageSource} />

                <View style={styles.padding0_10}>
                    <View style={styles.section}>
                        <View style={{ ...styles.header, ...styles.spaceBetween }}>
                            <View>
                                {!passenger && topLeftDetailsText ? <Text>{topLeftDetailsText}</Text> : <></>}
                                {passenger ? <PdfPassengerDetails passenger={passenger} /> : <></>}
                                {agentInfo ? <PdfBillingContactDetails {...agentInfo} /> : <></>}
                                {supplierInfo ? <PdfBillingContactDetails {...supplierInfo} /> : <></>}
                            </View>
                            <PdfHeaderDetails
                                iata={companyInfo?.iata}
                                issueDate={props.issueDate || createdAt}
                                dueDate={dueDate}
                                travelFileNumber={travelFileNumber}
                                travelFileOpenDate={travelFileOpenDate}
                                travelFileStartDate={travelFileStartDate}
                            />
                        </View>
                    </View>
                    <View style={[styles.alignItemsFlexEnd, styles.margin2_10]}>
                        <Text style={{ ...styles.button }}>{voucherCertification}</Text>
                    </View>

                    <View style={{ ...styles.section, ...styles.alignCenter }} wrap={false}>
                        <PdfMainDetails
                            topDetails={`${creditOrDebit ? creditOrDebit : voucherType} Note No. ${
                                voucherNumber || 'No Document Number'
                            }`}
                            withForLabel={!!forLabel}
                            bottomDetails={forLabel}
                        />
                    </View>

                    <View style={[styles.section, styles.marginBottom10px]}>
                        <PdfTableComponent columns={columns} rows={rows} summaryLine={summaryLine} />
                    </View>

                    <PdfRemarksSection body={remark} />

                    {showIssuedBy !== false && !!userName && <Text style={{ fontSize: BASE_FONT_SIZE - 1, color: '#6b7280', marginTop: 6, marginHorizontal: 10 }}>Issued by: {userName}</Text>}
                </View>

                <PdfFooter companyInfo={companyInfo} />
            </Page>
        </Document>
    );
};

export default memo(CreditDebitPdf);
